import { z } from 'zod';
import { listQuerySchema, objectId } from './common';
import type { PlanChangeRequestDto } from './customers';
import type { UserRef } from './users';

/**
 * Billing and Invoice (reference PC75), Purchase History (PC58) and Dealer Wallet (PC544).
 *
 * The reference bills through invoices, a tax engine and a payment gateway, none of which is
 * connected here. What exists: every payment taken (enrollment, equipment, wallet) with a
 * running invoice number, a customer wallet with its log, and a wallet per agent. Card
 * payments, refunds, discount credits, ad hoc invoices and autopay are not built.
 */

const amount = (label: string, max = 1_000_000) =>
  z
    .number({ invalid_type_error: `${label} is required` })
    .int()
    .min(1, `${label} is required`)
    .max(max);

/* ------------------------------ Customer billing --------------------------- */

/*
 * Billing And Invoice (PC75), as the reference's page lays it out (appendix 13.6): five menu
 * buttons (Invoices, Payment, Discount Credit, Wallet, More), a "BILLING SUMMARY" card, and
 * one list at a time. Every sale here is an invoice with a running number; an ad hoc invoice
 * is the one kind that starts unpaid, and "Pay Invoice(s)" settles it.
 */

/**
 * A card payment (switch `card-payments`): either a card typed into the payment gateway's own form
 * in the browser, which hands 212Ez only a token (Stripe's "pm_...", Authorize.Net's
 * "opaque:..."), or a saved card of the customer. A card number never reaches 212Ez.
 */
export const CARD_TOKEN =
  /^(pm_[A-Za-z0-9_]{3,255}|opaque:[A-Za-z0-9._-]{3,80}:[A-Za-z0-9+/=]{3,8000})$/;

export const cardPaymentSchema = z
  .object({
    paymentMethodId: z.string().trim().regex(CARD_TOKEN, 'Enter the card').optional(),
    savedCardId: z
      .string()
      .regex(/^[a-f\d]{24}$/i, 'Choose a saved card')
      .optional(),
    /** Keep the card typed in as a saved card of the customer (PC777). */
    save: z.boolean().optional().default(false),
  })
  .refine((c) => !!c.paymentMethodId !== !!c.savedCardId, {
    message: 'Enter the card or choose a saved card',
    path: ['paymentMethodId'],
  });
export type CardPaymentInput = z.input<typeof cardPaymentSchema>;
/** The card codes of the screens that take a card. */
export const CARD_CODES = {
  enrollment: 'PC319',
  planPurchase: 'PC404',
  restoreAndTopup: 'PC414',
  payInvoices: 'PC649',
  addWallet: 'PC568',
  saveCard: 'PC777',
} as const;

/** What the browser needs to show the gateway's card form. */
export interface PaymentConfigDto {
  /** The connected gateway, "simulated" on a test system, "none" otherwise. */
  gateway: 'stripe' | 'authorizenet' | 'simulated' | 'none';
  publishableKey: string;
  /** Authorize.Net's hosted card form: its public values and which of its systems to load. */
  authorizeNet?: { apiLoginId: string; clientKey: string; sandbox: boolean };
}

/**
 * "Add Wallet" (PC456): Amount and a Payment Mode. The reference's modes are Credit Card,
 * Cash, Using Pin, Add Wallet through Topup Card, Skip Payment (PC99) and Other Payment Method
 * (PC841); Cash and Skip Payment are built.
 */
export const WALLET_ADD_MODES = [
  { key: 'cash', label: 'Cash', code: null },
  { key: 'skip', label: 'Skip Payment', code: 'PC99' },
] as const;
export type WalletAddMode = (typeof WALLET_ADD_MODES)[number]['key'];
export const walletAddSchema = z
  .object({
    amountCents: amount('Amount'),
    mode: z.enum(['cash', 'skip', 'card']).optional().default('cash'),
    receiptNumber: z.string().trim().max(30).optional().default(''),
    card: cardPaymentSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.mode === 'card' && !v.card)
      ctx.addIssue({ code: 'custom', path: ['card'], message: 'Enter the card' });
    if (v.mode === 'cash' && !v.receiptNumber)
      ctx.addIssue({
        code: 'custom',
        path: ['receiptNumber'],
        message: 'Receipt Number is required',
      });
  });
export type WalletAddInput = z.input<typeof walletAddSchema>;

/**
 * "CSR Screen For Adjust Wallet" (PC765): Wallet Balance (shown), Adjust Amount and Reason.
 * The amount is signed: a minus takes money off.
 */
export const walletAdjustSchema = z.object({
  amountCents: z
    .number({ invalid_type_error: 'Adjust Amount is required' })
    .int()
    .min(-1_000_000)
    .max(1_000_000)
    .refine((v) => v !== 0, 'Adjust Amount is required'),
  reason: z.string().trim().min(1, 'Reason is required').max(250),
});
export type WalletAdjustInput = z.input<typeof walletAdjustSchema>;

/**
 * "Pay Invoice(s)" (PC67). The reference's Payment Types are Credit Card (PC649), Echeck
 * (PC651), Cash (PC650), Wallet Balance (PC652), Money Gram (PC825) and Cheque (PC823); the four
 * that need no gateway are built.
 */
export const BILL_PAY_METHODS = [
  { key: 'cash', label: 'Cash', code: 'PC650' },
  { key: 'wallet', label: 'Wallet Balance', code: 'PC652' },
  { key: 'moneyGram', label: 'Money Gram', code: 'PC825' },
  { key: 'cheque', label: 'Cheque', code: 'PC823' },
  { key: 'card', label: 'Credit Card', code: 'PC649' },
] as const;
export type BillPayMethod = (typeof BILL_PAY_METHODS)[number]['key'];
export const payInvoicesSchema = z.object({
  /** "Amount (A) ($)". */
  amountCents: amount('Amount'),
  method: z.enum(['cash', 'wallet', 'moneyGram', 'cheque', 'card'], {
    errorMap: () => ({ message: 'Select Payment Type' }),
  }),
  /** "Receipt Number" for cash and Money Gram, "Cheque Number" for a cheque. */
  receiptNumber: z.string().trim().max(30).optional().default(''),
  card: cardPaymentSchema.optional(),
});
export type PayInvoicesInput = z.input<typeof payInvoicesSchema>;

/**
 * "Add Ad hoc Invoice" (PC454), dialog "Add New Invoice". The reference's Invoice Type list
 * has 40 entries, most of which also do something (add minutes, a top-up, a Lifeline credit);
 * only the ones that are a plain charge are offered here.
 */
export const ADHOC_INVOICE_TYPES = [
  'Ad hoc - Invoice (Other)',
  'Credit Adj',
  'Feature Purchase',
] as const;
export const adhocInvoiceSchema = z.object({
  invoiceType: z.enum(ADHOC_INVOICE_TYPES, {
    errorMap: () => ({ message: 'Select Invoice Type' }),
  }),
  amountCents: amount('Amount'),
  /** "Do You want to include tax?": Yes taxes the amount at the customer's rate. */
  includeTax: z.boolean().optional().default(true),
  /** "Description Invoice", at most 100 characters. */
  description: z.string().trim().max(100).optional().default(''),
});
export type AdhocInvoiceInput = z.input<typeof adhocInvoiceSchema>;

/** "Ebill Setting" (PC460). */
export const EBILL_OPTIONS = ['Email', 'SMS', 'Both Email and SMS'] as const;
export type EBillOption = (typeof EBILL_OPTIONS)[number];
export const ebillSchema = z.object({
  eBill: z.enum(EBILL_OPTIONS, { errorMap: () => ({ message: 'Select E-Bill' }) }),
});

/**
 * "Add Discount Credit" (PC843), dialog "Discount Credit": Discount Credit Option (Add Credit,
 * Deduct Credit), Coupon Applicable Type (One Time, Recurring) with Recurring Month Count,
 * Amount, and a type from Manage Discount Credit Types. A one-time credit is a balance the
 * customer can pay with ("Pay Through Discount Credit" on Plan Renew and Plan Change); a
 * recurring credit comes off each plan purchase for its months ("Recurring Discount Log").
 */
export const DISCOUNT_CREDIT_OPTIONS = ['Add Credit', 'Deduct Credit'] as const;
export const DISCOUNT_APPLICABLE_TYPES = ['One Time', 'Recurring'] as const;
export const discountCreditSchema = z.object({
  option: z.enum(DISCOUNT_CREDIT_OPTIONS, {
    errorMap: () => ({ message: 'Select Discount Credit Option' }),
  }),
  applicableType: z.enum(DISCOUNT_APPLICABLE_TYPES).optional().default('One Time'),
  /** "Recurring Month Count", at most two digits; asked for a recurring credit. */
  recurringMonths: z.coerce.number().int().min(0).max(99).optional().default(0),
  amountCents: amount('Amount'),
  typeId: z.string().regex(/^[a-f\d]{24}$/i, 'Choose Discount Credit Type'),
});
export type DiscountCreditInput = z.input<typeof discountCreditSchema>;

/** One line of "Discount Credit Log" (843). */
export interface DiscountCreditLogDto {
  id: string;
  at: string;
  /** Add Credit, Deduct Credit, or Used (paid part of an invoice). */
  option: 'Add Credit' | 'Deduct Credit' | 'Used';
  applicableType: 'One Time' | 'Recurring';
  type: string;
  /** Signed: negative when the balance went down. */
  amountCents: number;
  balanceAfterCents: number;
  months: number;
  invoiceNo: string;
  by: UserRef | null;
}

/** One line of "Recurring Discount Log" (843): a recurring credit and how much of it is left. */
export interface RecurringDiscountDto {
  id: string;
  type: string;
  amountCents: number;
  months: number;
  remaining: number;
  status: 'Active' | 'Ended';
  at: string;
  by: UserRef | null;
}

/** What an invoice is for. `adhoc` starts unpaid; `billpay` is "Pay Invoice(s)". */
export type InvoicePurpose =
  'enrollment' | 'equipment' | 'wallet' | 'topup' | 'renew' | 'change' | 'adhoc' | 'billpay';

/**
 * One row of "Regular Invoices" (PC72), with what the expanded row shows. The columns of the
 * reference: Invoice No. (Generated), Invoice Type, Paid / Unpaid Status, Inv Balance, Amount
 * Paid($), Total Due($) with Due Date, Action (PC609).
 */
export interface InvoiceDto {
  id: string;
  invoiceNo: string;
  /** "Generated" and "Created Date". */
  date: string;
  /** "Invoice Type", in the reference's words ("New Signup Plan Purchase", "CSR Bill Pay"). */
  invoiceType: string;
  /** "Description". */
  description: string;
  purpose: InvoicePurpose;
  status: 'Paid' | 'Unpaid' | 'Void';
  /** How and when it was paid; empty while unpaid. */
  paidMethod: string;
  paidAt: string | null;
  /** Debit (A), Tax (B), Processing Fee (C); Inv Balance D = A + B + C. */
  debitCents: number;
  taxCents: number;
  processingFeeCents: number;
  balanceCents: number;
  /** Amount Paid (E) and Total Due (D - E). */
  amountPaidCents: number;
  totalDueCents: number;
  dueDate: string;
  /** "Source": where the sale was made (Handover, CSR, Auto Pay). */
  source: string;
  receiptNumber: string;
  createdBy: UserRef | null;
  /** What has been refunded on it, and whether more may be (switch `payment-refunds`). */
  refundedCents: number;
  refundable: boolean;
}

/**
 * One row of "Payments" (PC463): Invoice No. with Created, Invoice Type, Amount, Tax, Payment
 * Amount, Payment Type, Payment By with Source, Transaction ID, Action.
 */
export interface PaymentRowDto {
  id: string;
  invoiceNo: string;
  date: string;
  invoiceType: string;
  description: string;
  /** "Principal / invoice amount", tax and fee, and what was taken ("Payment amount (CR)"). */
  amountCents: number;
  taxCents: number;
  processingFeeCents: number;
  paymentCents: number;
  method: string;
  paymentBy: UserRef | null;
  source: string;
  transactionId: string;
  /** "Payment mode": SINGLE here; a split payment is not built. */
  paymentMode: 'SINGLE';
}

/** "Wallet Log" (PC466): # | Amount | Date | Type | Purchase Type | Transaction ID | Invoice Number | Created By | Source. */
export interface WalletLogDto {
  id: string;
  type: 'Add Wallet' | 'Adjust Wallet' | 'Payment';
  /** Signed: negative for a deduction. */
  amountCents: number;
  balanceAfterCents: number;
  /** "Purchase Type": what the money was for. */
  purchaseType: string;
  note: string;
  transactionId: string;
  invoiceNo: string;
  by: UserRef | null;
  source: string;
  at: string;
}

/** "BILLING SUMMARY": the latest invoice, what is still due, the plan. */
export interface BillingSummaryDto {
  current: {
    invoiceNo: string;
    amountCents: number;
    status: 'Paid' | 'Unpaid';
    dueDate: string;
    paidAt: string | null;
    method: string;
    debitCents: number;
  } | null;
  /** "net due": what the unpaid invoices still owe. */
  netDueCents: number;
  plan: { name: string; priceCents: number } | null;
}

export interface CustomerBillingDto {
  walletBalanceCents: number;
  /** "Discount Credit: $": the one-time credit the customer can pay with. */
  discountCreditCents: number;
  discountCreditLog: DiscountCreditLogDto[];
  recurringDiscounts: RecurringDiscountDto[];
  summary: BillingSummaryDto;
  invoices: InvoiceDto[];
  payments: PaymentRowDto[];
  walletLog: WalletLogDto[];
  eBill: EBillOption;
  /** What "Pay Invoice(s)" offers by default: the amount due, or the plan amount. */
  payDefaultCents: number;
}

/* -------------------------------- Dealer wallet ---------------------------- */

/** "Add Funds": Amount ($)* and Receipt Number*. Cash (PC569); card needs a gateway. */
export const dealerAddFundsSchema = z.object({
  amountCents: amount('Amount', 10_000_000),
  receiptNumber: z.string().trim().min(1, 'Receipt Number is required').max(30),
});

const blankId = z.preprocess((v) => (v === '' || v === null ? undefined : v), objectId.optional());
/** "Other User ID": a login ID typed instead of choosing from the list (release switch `dealer-wallet-share`). */
const otherUserId = z.string().trim().max(100).default('');
const someUser = (v: { toUserId?: string; otherUserId: string }, ctx: z.RefinementCtx) => {
  if (!v.toUserId && !v.otherUserId)
    ctx.addIssue({ code: 'custom', path: ['toUserId'], message: 'Select a User' });
};

/** "Transfer Funds": Transfer To* (or Other User ID) and Amount ($)*. */
export const dealerTransferSchema = z
  .object({ toUserId: blankId, otherUserId, amountCents: amount('Amount', 10_000_000) })
  .superRefine(someUser);

/** "Share Wallet": Select User* or Other User ID. */
export const dealerShareSchema = z.object({ toUserId: blankId, otherUserId }).superRefine(someUser);

/**
 * "Confirm Fund Removal": Amount ($)*. From Remove Funds' list of wallets it names the wallet;
 * absent means the caller's own, as before.
 */
export const dealerRemoveFundsSchema = z.object({
  amountCents: amount('Amount', 10_000_000),
  walletUserId: blankId,
});

const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date')
  .optional();

/** Transaction History filter: Record Limit, Select Wallet, From Date, To Date. */
export const dealerTxnQuerySchema = listQuerySchema.extend({
  walletUserId: objectId.optional(),
  dateFrom: day,
  dateTo: day,
});

export const DEALER_TXN_TYPES = [
  'Add Funds',
  'Transfer Out',
  'Transfer In',
  'Remove Funds',
  // Ours: an enrollment paid with "Dealer wallet" on step 4 (the reference's name was not seen).
  'Enrollment Payment',
  // Earned commission moved into the wallet ("Set Matrix Commission Redeem Rule").
  'Commission Redeem',
] as const;
export type DealerTxnType = (typeof DEALER_TXN_TYPES)[number];

export interface DealerWalletDto {
  balanceCents: number;
  /** Wallets the caller may pick in "Select Wallet" and "Transfer To": own and below. */
  wallets: Array<{ userId: string; name: string; loginId: string; balanceCents: number }>;
  /** Whether the caller's wallet exists; the reference asks to "Create your wallet" first. */
  hasWallet: boolean;
  /** Its "Effective Wallet ID"; 0 before it exists. */
  walletId: number;
  /** A wallet another user shared with the caller: it pays the caller's dealer-wallet orders. */
  sharedWallet: {
    userId: string;
    name: string;
    loginId: string;
    walletId: number;
    balanceCents: number;
  } | null;
  /** The users the caller's wallet is shared with. */
  sharedWith: UserRef[];
}

/** One wallet of Remove Funds' list: S.No | Effective Wallet ID | Balance | Action. */
export interface DealerWalletRowDto {
  userId: string;
  name: string;
  loginId: string;
  walletId: number;
  balanceCents: number;
}

/** Remove Funds' filter: Record Limit and Select Wallet. */
export const dealerWalletListQuerySchema = listQuerySchema.extend({
  walletUserId: blankId,
});

/** One line of the Activity Log: what was done to a wallet other than moving money. */
export interface DealerWalletLogDto {
  id: string;
  wallet: UserRef | null;
  action: string;
  description: string;
  by: UserRef | null;
  at: string;
}

export interface DealerTxnDto {
  id: string;
  wallet: UserRef | null;
  type: DealerTxnType;
  /** Signed: negative for money leaving the wallet. */
  amountCents: number;
  balanceAfterCents: number;
  counterparty: UserRef | null;
  receiptNumber: string;
  by: UserRef | null;
  at: string;
}

/* ------------------------- Charges on an existing line ---------------------- */

/**
 * How a top-up or a plan renewal can be paid without a payment gateway. `feeType` is the
 * "Payment Type" of Payment Processing Fee Config. Card payments are not built.
 */
export const LINE_PAYMENT_METHODS = [
  { key: 'cash', label: 'Cash', feeType: 'Cash' },
  { key: 'moneyOrder', label: 'Money Order', feeType: 'Money Order' },
  { key: 'moneyGram', label: 'Money Gram', feeType: 'Money Gram' },
  { key: 'wallet', label: 'Wallet', feeType: 'Wallet' },
  // Charged through the company's payment gateway (Settings > Connections; switch card-payments).
  { key: 'card', label: 'Credit Card', feeType: 'Credit Card' },
] as const;
export type LinePaymentMethod = (typeof LINE_PAYMENT_METHODS)[number]['key'];
export const LINE_PAYMENT_KEYS = LINE_PAYMENT_METHODS.map((m) => m.key) as [
  LinePaymentMethod,
  ...LinePaymentMethod[],
];
export const linePaymentLabel = (key: string): string =>
  key === 'skip'
    ? 'Skip Payment'
    : key === 'dealerWallet'
      ? 'Dealer Wallet'
      : (LINE_PAYMENT_METHODS.find((m) => m.key === key)?.label ?? key);

/**
 * What a charge comes to. These rules are this project's own; the reference's calculation
 * was never observed. Discounts come off first, tax is worked out on what is left, and the
 * processing fee is added last and is not taxed.
 */
export interface ChargeQuoteDto {
  /** The list price of what is bought. */
  baseCents: number;
  /** Named reductions, in the order they were applied. */
  discounts: Array<{ label: string; amountCents: number }>;
  discountCents: number;
  /**
   * "Other Charges" of the reference's Plan Renew screen (Late Fee, Restore Fee), added to
   * the price before tax. Empty for every other kind of charge.
   */
  charges: Array<{ label: string; amountCents: number }>;
  chargesCents: number;
  taxCents: number;
  /** Rate used, as a percentage (7.25 for 7.25%); 0 when no tax applied. */
  taxRatePercent: number;
  /**
   * Set when the tax engine (CereTax) worked the tax out: the kind of charge and the amount it
   * taxed, so the sale can be posted to it once paid.
   */
  taxBasis?: {
    kind: 'signup' | 'topup' | 'renew' | 'equipment';
    taxableCents: number;
    /** The part of it that is fees (late fee, restore fee). */
    feesCents?: number;
    /** A plan's own tax codes (`TaxCodes` of integrations/tax.ts). */
    codes?: {
      lines?: Array<{ psCode: string; percent: number }>;
      businessType?: string;
      customerType?: string;
    };
  };
  processingFeeCents: number;
  totalCents: number;
}

/* ------------------------ Plan Renew and Plan Change ---------------------- */

/**
 * The tabs of the reference's "CSR Screen For Plan Renew" and "CSR Screen For Plan Change"
 * (appendix 13.7), each with its permission. These two screens have no Cash tab; Credit Card
 * (PC404) and Dealer Wallet (PC544) are not built.
 */
export const PLAN_PAYMENT_CODES: Partial<Record<LinePaymentMethod, string>> = {
  moneyOrder: 'PC410',
  moneyGram: 'PC411',
  wallet: 'PC412',
  // Credit Card (switch card-payments): the reference's code on these two screens.
  card: 'PC404',
};
/**
 * The ways of paying of "Payment Information" on Update Account Status (PC95) when a suspended
 * line is restored, with the reference's codes (appendix 13.8). Credit Card (PC414), Dealer Wallet
 * (PC544) and Other Payment Option (PC841) are not offered: no gateway, and the other two were not
 * seen at work.
 */
export const RESTORE_PAYMENT_CODES: Partial<Record<LinePaymentMethod, string>> = {
  cash: 'PC415',
  moneyOrder: 'PC416',
  moneyGram: 'PC417',
  wallet: 'PC418',
  card: 'PC414',
};
/** "Skip Payment (PC99)": the invoice is made and marked paid with the method SKIP. */
export const SKIP_PAYMENT_CODE = 'PC99';
/** "Plan Renew Mode" and "Plan Change Mode (PC518)", with their permissions. */
export const PLAN_PURCHASE_MODES = [
  { key: 'Instant', code: 'PC628' },
  { key: 'On Expiry', code: 'PC629' },
  { key: 'Custom Date', code: 'PC630' },
] as const;
export type PlanPurchaseMode = (typeof PLAN_PURCHASE_MODES)[number]['key'];
const MODE_KEYS = PLAN_PURCHASE_MODES.map((m) => m.key) as [
  PlanPurchaseMode,
  ...PlanPurchaseMode[],
];
/** "Keep the Expiry as Original Date (PC631)". */
export const KEEP_EXPIRY_CODE = 'PC631';
/** The days one plan purchase lasts: the same 30-day cycle an enrollment starts with. */
export const PLAN_CYCLE_DAYS = 30;

/** A switch that arrives as a boolean in a body and as "true"/"false" in a query. */
const flag = (fallback: boolean) =>
  z.preprocess(
    (v) => (v === undefined || v === '' ? fallback : v === true || v === 'true'),
    z.boolean(),
  );

const planPurchaseFields = {
  /** One of the tabs; empty when the payment is skipped. The server checks the pair. */
  method: z.enum(LINE_PAYMENT_KEYS).or(z.literal('')).optional().default(''),
  card: cardPaymentSchema.optional(),
  receiptNumber: z.string().trim().max(30).optional().default(''),
  couponCode: z.string().trim().max(30).optional().default(''),
  mode: z
    .enum(MODE_KEYS, { errorMap: () => ({ message: 'Select Plan Renew Mode' }) })
    .optional()
    .default('Instant'),
  /** The day of a Custom Date request, YYYY-MM-DD. */
  effectiveDate: z
    .string()
    .regex(/^(\d{4}-\d{2}-\d{2})?$/, 'Enter a valid date')
    .optional()
    .default(''),
  keepExpiry: flag(true),
  skipPayment: flag(false),
  skipLateFee: flag(false),
  skipRestoreFee: flag(false),
  /** "Pay Through Discount Credit": the credit pays first, the way of paying the rest. */
  payByDiscountCredit: flag(false),
};

/**
 * Plan Renew (PC519), field for field as the reference's "CSR Screen For Plan Renew"
 * (appendix 13.7): a way of paying (or Skip Payment), the mode, "Keep the Expiry as
 * Original Date", and the two "Other Charges" with their skip boxes. A coupon is ours.
 */
export const planRenewSchema = z.object(planPurchaseFields);
export type PlanRenewInput = z.input<typeof planRenewSchema>;
/**
 * Plan Change (PC77): the same screen with "Change Plan To". The reference takes the money
 * when the change is asked for, also for an "On Expiry" change (its activity log says so).
 */
export const planChangeSchema = z.object({
  planId: z.string().regex(/^[a-f\d]{24}$/i, 'Select Plan'),
  ...planPurchaseFields,
});
export type PlanChangeInput = z.input<typeof planChangeSchema>;

export interface PlanRenewalDto {
  id: string;
  invoiceNo: string;
  plan: string;
  amountCents: number;
  discountCents: number;
  taxCents: number;
  processingFeeCents: number;
  method: string;
  /** The plan expiry this renewal produced. */
  expiresAt: string | null;
  at: string;
  by: UserRef | null;
}

/** What the Plan Renew and Plan Change screens show before anything is bought. */
export interface PlanPurchaseScreenDto {
  /** The plan the line is on. */
  plan: { id: string; name: string; priceCents: number } | null;
  /** Plan Change only: the plans the line can move to ("Change Plan To"). */
  plans: Array<{ id: string; name: string; code: number; priceCents: number }>;
  /** "Existing Plan Expiry Date". */
  planExpiresAt: string | null;
  /** The plan has run out: only Instant is possible, and the new cycle starts now. */
  expired: boolean;
  walletBalanceCents: number;
  /** "Discount Credit Balance". */
  discountCreditCents: number;
  /** "Late Fee Charge" (SC269, SC270) and "Restore Fee Charge" (SC271, SC272); 0 when not due. */
  lateFeeCents: number;
  restoreFeeCents: number;
  /** Default of "Keep the Expiry as Original Date" (SC252 for renew, SC254 for change). */
  keepExpiryDefault: boolean;
  /** Instant and Custom Date for a plan that has not run out (SC251, SC253). */
  instantAllowed: boolean;
  /** Why nothing can be bought, or '' when it can. */
  blocked: string;
  /** "Advance Plan List": the requests of this screen, newest first. */
  requests: PlanChangeRequestDto[];
  /** Plan Renew only: earlier renewals. */
  renewals: PlanRenewalDto[];
}

/** What a renewal or a plan change came to. */
export interface PlanPurchaseResultDto {
  /** Completed now (Instant), or waiting for its day. */
  status: 'Completed' | 'Pending';
  /** The new plan expiry, once applied. */
  expiresAt: string | null;
  /** The day the purchase takes effect, YYYY-MM-DD. */
  effectiveDate: string;
  invoiceNo: string;
  quote: ChargeQuoteDto;
  /** The part of the total paid with discount credit. */
  discountCreditCents: number;
  /** The row this purchase added to the "Advance Plan List". */
  request: PlanChangeRequestDto;
}

/** One use of a coupon ("Coupons log", PC1195). */
export interface CouponRedemptionDto {
  id: string;
  code: string;
  couponName: string;
  appliedOn: string;
  discountCents: number;
  invoiceNo: string;
  at: string;
  by: UserRef | null;
}

/* -------------------------------- Auto Pay (PC574) ------------------------- */

/**
 * Auto Pay (PC574). The reference charges a saved card on a day of the month or when a
 * balance runs low; no payment gateway is connected, so here Auto Pay is one switch: renew
 * the plan from the customer's wallet when it expires.
 */
export const autoPaySchema = z.object({ enabled: z.boolean() });

export interface AutoPayDto {
  enabled: boolean;
  since: string | null;
  plan: { name: string; priceCents: number } | null;
  planExpiresAt: string | null;
  walletBalanceCents: number;
}

/* ------------------- Billing and Invoice: logs and settings ---------------- */

/** "Autopay Log" (PC678): Auto Pay switched on or off, renewals it paid, attempts it could not. */
export interface AutopayLogDto {
  id: string;
  at: string;
  event: 'Switched on' | 'Cancelled' | 'Paid' | 'Failed';
  amountCents: number | null;
  invoiceNo: number | null;
  reason: string;
  by: UserRef | null;
}

/** "Declined Payment" (PC932): a payment that was tried and refused. */
export interface DeclinedPaymentDto {
  id: string;
  at: string;
  paymentFor: string;
  method: string;
  reason: string;
}

/** "OCS Purchase" (PC465): purchases the carrier's charging system holds for the line. */
export interface OcsPurchaseDto {
  at: string;
  product: string;
  amountCents: number;
  status: string;
}
export interface OcsPurchasesDto {
  /** The reference's message when the account has no carrier ("Carrier not specified."). */
  message: string;
  data: OcsPurchaseDto[];
}
export const CARRIER_NOT_SPECIFIED = 'Carrier not specified.';

/**
 * The kinds of charge a customer can be exempted from tax on ("Tax Exemption Setting", PC459).
 * Assumption: the reference loads its list per state from its tax service; here tax is one rate
 * per state and kind of charge (Tax Config > Tax Rates and the System Config switches), so the
 * exemption is per kind of charge. A new signup is not listed: the customer exists after it.
 */
export const TAX_EXEMPT_KINDS = [
  { kind: 'renew', label: 'Plan Renew / Plan Change' },
  { kind: 'topup', label: 'Top-up' },
  { kind: 'equipment', label: 'Equipment Purchase' },
] as const;
export type TaxExemptKind = (typeof TAX_EXEMPT_KINDS)[number]['kind'];

export interface TaxExemptionDto {
  state: string;
  items: Array<{ kind: TaxExemptKind; label: string; ratePercent: number; exempt: boolean }>;
}
export const taxExemptionSchema = z.object({
  kinds: z
    .array(z.enum(['renew', 'topup', 'equipment']))
    .max(3)
    .transform((list) => [...new Set(list)]),
});
export type TaxExemptionInput = z.input<typeof taxExemptionSchema>;

/**
 * "Apply Coupon" (PC1195) on Billing and Invoice. Assumption: the reference section was seen
 * loading only; here a coupon is applied to one unpaid invoice.
 */
export const invoiceCouponSchema = z.object({
  invoiceNo: z.coerce.number().int().min(1, 'Select Invoice'),
  couponCode: z.string().trim().min(1, 'Enter Coupon Code').max(40),
});
export type InvoiceCouponInput = z.input<typeof invoiceCouponSchema>;

/* ------------------------------ Refunds (2026-10-29) ----------------------------- */

/**
 * "Refund" on a paid invoice (switch `payment-refunds`). Assumption, the reference's refund screen
 * was not seen (only the Payment Refunds Report): Full refunds what is left of what was paid,
 * Partial an amount up to that; a reason is required. 212Ez records the refund and gives it a
 * refund invoice number. A card payment is refunded to the card through the payment gateway
 * (switch card-payments); cash, money order, Money Gram or cheque go back by hand, outside 212Ez. A wallet payment is given back with
 * the wallet adjustment instead.
 */
export const REFUND_TYPES = ['Full', 'Partial'] as const;
export type RefundType = (typeof REFUND_TYPES)[number];
export const REFUNDABLE_METHODS = ['cash', 'moneyOrder', 'moneyGram', 'cheque', 'card'] as const;

export const refundSchema = z
  .object({
    refundType: z.string().refine((v) => (REFUND_TYPES as readonly string[]).includes(v), {
      message: 'Select Refund Type',
    }),
    amountCents: z.number().int().positive('Enter an amount above 0').nullable().default(null),
    reason: z
      .string()
      .trim()
      .min(1, 'Refund Reason is required')
      .max(200, 'Refund Reason may have up to 200 characters'),
  })
  .superRefine((v, ctx) => {
    if (v.refundType === 'Partial' && v.amountCents === null)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amountCents'],
        message: 'Enter the amount to refund',
      });
  });
export type RefundInput = z.input<typeof refundSchema>;
